<template>
  <Alert
    v-if="showAlertPostalCode"
    class="my-3"
    :message="`${t('SHARED.POSTAL_CODE_LABEL')}: ` + alertMessagePostalCode"
    :alertType="alertTypePostalCode"
    :closable="alertClosablePostalCode"
    @onClose="dismissAlertPostalCode"
  />
  <div>
    <h2>{{ t("SHARED.USER_ADDRESS") }}</h2>
    <div class="row mt-3">
      <div
        v-if="showPostalCode && shouldLookupAddressDetailsUsingPostalCode()"
        class="col-md-12 mb-3"
      >
        <InputFieldZipCode
          data-testid="customer-address-postal-code"
          :className="'col me-1'"
          :labelName="t('SHARED.USER_POSTAL_CODE_FIELD')"
          :placeholder="t('SHARED.USER_CONTACT_PLACEHOLDER_POSTAL_CODE')"
          :inputId="'postalCode'"
          :required="true"
          v-model="postalCodeModel"
          @update:model-value="setPostalCode"
        />
        <Alert
          v-if="showCearaStateError"
          class="mt-2"
          :message="cearaStateErrorMessage"
          alertType="alert-danger"
          :closable="false"
        />
        <div v-if="showCearaStateError" class="mt-2">
          <a
            href="https://www.payjoy.com/br/celulares#find-store"
            target="_blank"
            rel="noopener noreferrer"
            class="ceara-store-link"
          >
            {{ t("SHARED.FIND_CLOSEST_STORE") }}
          </a>
        </div>
      </div>
      <div class="col-md-12 mb-3">
        <InputFieldText
          data-testid="customer-address-addressline"
          :labelName="t('SHARED.USER_ADDRESS_FIELD')"
          :placeholder="t('SHARED.USER_CONTACT_PLACEHOLDER_ADDRESS')"
          inputId="address"
          @update:modelValue="setAddress"
          @change="addressChanged"
          v-model="addressLineModel"
          :disabled="disabled"
          :readonly="disabled"
        />
      </div>
      <div class="col-md-12 mb-3">
        <InputFieldText
          data-testid="customer-address-neighborhood"
          :labelName="t('SHARED.USER_NEIGHBORHOOD_FIELD')"
          :placeholder="t('SHARED.USER_CONTACT_PLACEHOLDER_NEIGHBORHOOD')"
          inputId="neighborhood"
          @update:modelValue="setNeighborhood"
          @change="neighbourhoodChanged"
          v-model="neighborhoodModel"
          :readonly="readOnlyNeighborhood"
        />
      </div>
      <div class="col-md-12 mb-3">
        <div v-if="!readOnly">
          <Dropdown
            data-testid="customer-address-state"
            :dropdown-Label="t('SHARED.USER_STATE_FIELD')"
            :options="states"
            v-model="stateObjectModel"
            :defaultValue="stateObjectModel"
            @update:modelValue="setSelectedState"
            :key="componentKey"
          />
        </div>
        <div v-else>
          <InputFieldText
            data-testid="customer-address-state-display"
            :labelName="t('SHARED.USER_STATE_FIELD')"
            :placeholder="t('SHARED.USER_CONTACT_PLACEHOLDER_STATE')"
            inputId="state"
            v-model="stateObjectModel.name"
            :readonly="readOnly"
          />
        </div>
      </div>
      <div class="col-md-12 mb-3">
        <div v-if="!readOnly">
          <Dropdown
            data-testid="customer-address-city"
            :dropdown-label="t('SHARED.USER_CITY_FIELD')"
            :options="cities"
            v-model="cityObjectModel"
            :defaultValue="cityObjectModel"
            @update:modelValue="setSelectedCity"
            :disabled="cityDisabled"
          />
        </div>
        <div v-else>
          <InputFieldText
            data-testid="customer-address-city-display"
            :labelName="t('SHARED.USER_CITY_FIELD')"
            :placeholder="t('SHARED.USER_CONTACT_PLACEHOLDER_CITY')"
            inputId="city"
            v-model="cityObjectModel.name"
            :readonly="readOnly"
          />
        </div>
      </div>
      <div
        v-if="showPostalCode && !shouldLookupAddressDetailsUsingPostalCode()"
        class="col-md-12 mb-3"
      >
        <InputFieldZipCode
          data-testid="customer-address-postal-code"
          :className="'col me-1'"
          :labelName="t('SHARED.USER_POSTAL_CODE_FIELD')"
          :placeholder="t('SHARED.USER_CONTACT_PLACEHOLDER_POSTAL_CODE')"
          :inputId="'postalCode'"
          :required="true"
          v-model="postalCodeModel"
          @update:model-value="setPostalCode"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import InputFieldText from "@/apps/merchant3/shared-components/input-fields/InputFieldText.vue";
import InputFieldZipCode from "@/apps/merchant3/shared-components/input-fields/InputFieldZipCode.vue";
import { onMounted, ref, computed, onBeforeMount, PropType, Ref } from "vue";
import { onUpdated } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import Dropdown from "@/apps/merchant3/shared-components/ui/Dropdown.vue";
import { IOptionDropdown } from "@/types/dropdown";
import { useState } from "@/apps/merchant3/shared-hooks/useState";
import { useCity } from "@/apps/merchant3/shared-hooks/useCity";
import scrollToBottom from "@/apps/merchant3/shared-helpers/scrollToBottom";
import logEvent from "@/apps/merchant3/services/event-logs.service";
import lookupPostalCode from "@/apps/merchant3/services/postalCode.service";
import EventLogs from "@/apps/merchant3/types/EventLogs";
import ICustomerAddress from "@/apps/merchant3/types/ICustomerAddress";
import { AxiosError, AxiosResponse } from "axios";
import { COUNTRIES } from "@/helpers/countries";
import Alert from "@/apps/merchant3/shared-components/common/Alert.vue";
import { getAPIErrorMessage } from "@/apps/merchant3/shared-helpers/APIErrorMessages";
import { MerchantCountry } from "@/types/merchant";
import { PostalCodeLength } from "@/types/addressType";
import { isDirectToCustomerFlow } from "@/apps/merchant3/helpers/checkoutFlows";

const props = defineProps({
  showPostalCode: { type: Boolean, default: false },
  address: { type: Object as PropType<ICustomerAddress> },
});
const emit = defineEmits(["customerAddress", "blockContinueForCeara"]);
const store = useStore();
const { t } = useI18n();
const states = ref();
const cities = ref([]);

const componentKey = ref(0);
const addressLineModel = ref();
const neighborhoodModel = ref();
const stateObjectModel: Ref<{ name: string; code: string }> = ref({
  name: "",
  code: "",
});
const cityObjectModel: Ref<{ name: string }> = ref({ name: "" });
const cityDisabled = ref(true);
const postalCodeModel = ref();
const postalCodePriorValue = ref("");
const postalCodeDataRead = ref(false);
const neighborhoodIsEmpty = ref(false);
const alertTypePostalCode = ref("");
const alertMessagePostalCode = ref("");
const showAlertPostalCode = ref(false);
const alertClosablePostalCode = ref(true);
const country = computed(() => store?.state.merchant?.merchant?.country);
const showCearaStateError = ref(false);
const cearaStateErrorMessage = computed(() =>
  t("SHARED.CEARA_STATE_ERROR_MESSAGE")
);

// TODO(PJ-CEARA): Temporarily disabled — a legal ruling currently allows D2C in Ceará, but this may be reversed soon so the logic is preserved behind this flag.
const isCearaRejectionEnabled = false;

const shouldShowCearaError = (stateCode: string): boolean => {
  if (!isCearaRejectionEnabled) return false;

  const normalizedCode = stateCode.includes("-")
    ? stateCode.split("-")[1]
    : stateCode;
  return (
    country.value === MerchantCountry.BR &&
    normalizedCode === "CE" &&
    isDirectToCustomerFlow.value
  );
};

const fullAddress = computed(() => ({
  neighborhood: neighborhoodModel.value,
  city: cityObjectModel.value.name,
  state: stateObjectModel.value.name,
  stateCode: stateObjectModel.value.code,
  address: addressLineModel.value,
  postalCode: postalCodeModel.value,
}));

const readOnly = computed(
  () => store?.state.merchant?.merchant?.country === MerchantCountry.BR
);

const readOnlyNeighborhood = computed(
  () =>
    store?.state.merchant?.merchant?.country === MerchantCountry.BR &&
    !neighborhoodIsEmpty.value
);

const disabled = computed(() => readOnly.value && !postalCodeDataRead.value);

// Counter to prevent scrolling when the page loads first time.
const renderCounter = ref(0);
const forceRerender = () => {
  componentKey.value += 1;
};
const merchantCountry = computed(
  () => store?.state.merchant?.merchant?.country || ""
);

const addressChanged = () => {
  logEvent(EventLogs.Events.CUSTOMER_ADDRESS_ENTERED, {
    address: addressLineModel.value,
  });
};

const setSelectedCity = (value: IOptionDropdown) => {
  store.dispatch("customer/setCustomerCity", value);
  logEvent(EventLogs.Events.CUSTOMER_CITY_SELECTED, {
    city: value.name,
  });

  forceRerender();
  if (renderCounter.value > 0) {
    scrollToBottom();
  }

  emit("customerAddress", fullAddress.value);
};

const neighbourhoodChanged = () => {
  logEvent(EventLogs.Events.CUSTOMER_NEIGHBORNHOOD_ENTERED, {
    neighborhood: neighborhoodModel.value,
  });
};

const fetchCities = (state: string) => {
  cityDisabled.value = true;

  const { selectedCities, getCities } = useCity();
  getCities(merchantCountry.value, state);
  cities.value = selectedCities.value || [];

  if (cities.value.length > 0) {
    cityDisabled.value = false;
  }
};

const resetSelectedCity = (state: string) => {
  store.dispatch("customer/resetCustomerCity");
  cityObjectModel.value.name = "";
  fetchCities(state);
};

const setSelectedState = (value: IOptionDropdown) => {
  resetSelectedCity(value.name);

  store.dispatch("customer/setCustomerState", value);
  logEvent(EventLogs.Events.CUSTOMER_STATE_SELECTED, {
    state: value.name,
  });

  if (shouldShowCearaError(value.code)) {
    showCearaStateError.value = true;
    emit("blockContinueForCeara", true);
  } else {
    showCearaStateError.value = false;
    emit("blockContinueForCeara", false);
  }

  emit("customerAddress", fullAddress.value);

  forceRerender();
  if (renderCounter.value > 0) scrollToBottom();
};

const setAddress = () => {
  emit("customerAddress", fullAddress.value);
};

const setPostalCode = (newValue: string) => {
  if (newValue === "") {
    postalCodeModel.value = null;
    postalCodeDataRead.value = false;
  }
  if (newValue === postalCodePriorValue.value) {
    return;
  }
  postalCodePriorValue.value = newValue;
  if (shouldLookupAddressDetailsUsingPostalCode()) {
    lookupAddressDetailsByPostalCode(newValue);
  } else {
    emit("customerAddress", fullAddress.value);
  }
};

const dismissAlertPostalCode = () => {
  showAlertPostalCode.value = false;
};

const shouldLookupAddressDetailsUsingPostalCode = () => {
  return country.value === MerchantCountry.BR;
};

const lookupAddressDetailsByPostalCode = (postalCode: string) => {
  if (
    !postalCode ||
    postalCode.length !== Number(PostalCodeLength[country.value])
  ) {
    return;
  }

  lookupPostalCode(postalCode, country.value)
    .then((response: AxiosResponse) => {
      showAlertPostalCode.value = false;
      postalCodeDataRead.value = true;
      updateCityStateNeighborhood(response.data, country.value);
      emit("customerAddress", fullAddress.value);
    })
    .catch((error: AxiosError) => {
      resetCityStateNeighborhood();
      alertTypePostalCode.value = "alert-danger";
      alertMessagePostalCode.value = getAPIErrorMessage(
        error.response?.data,
        t
      );
      showAlertPostalCode.value = true;
    });
};

const updateCityStateNeighborhood = (
  data: { state: string; city: string; neighborhood: string },
  country: string
) => {
  const fullStateCode = data.state.includes("-")
    ? data.state
    : `BR-${data.state}`;
  stateObjectModel.value.code = fullStateCode;

  const statesList = COUNTRIES[country]?.states;
  let stateName = "";
  for (let si in statesList) {
    if (statesList[si].code === fullStateCode) {
      stateObjectModel.value.name = statesList[si].name;
      stateName = statesList[si].name;
      break;
    }
  }

  store.dispatch("customer/setCustomerState", {
    code: fullStateCode,
    name: stateName,
  });

  store.dispatch("customer/setCustomerCity", {
    name: data.city,
  });
  cityObjectModel.value.name = data.city;
  if (data.neighborhood || data.neighborhood !== "") {
    neighborhoodModel.value = data.neighborhood;
  } else {
    neighborhoodIsEmpty.value = true;
  }

  if (shouldShowCearaError(fullStateCode)) {
    showCearaStateError.value = true;
    emit("blockContinueForCeara", true);
  } else {
    showCearaStateError.value = false;
    emit("blockContinueForCeara", false);
  }
};

const resetCityStateNeighborhood = () => {
  store.dispatch("customer/resetCustomerCity");
  cityObjectModel.value.name = "";
  stateObjectModel.value.name = "";
  stateObjectModel.value.code = "";
  neighborhoodModel.value = null;
  neighborhoodIsEmpty.value = false;
  showCearaStateError.value = false;
  emit("blockContinueForCeara", false);
};

const setNeighborhood = () => {
  emit("customerAddress", fullAddress.value);
};

onBeforeMount(() => {
  const { selectedStates, getStateList } = useState();
  getStateList(merchantCountry.value);
  states.value = selectedStates.value || [];
});

onMounted(() => {
  addressLineModel.value = props?.address?.street1 ?? null;
  neighborhoodModel.value = props?.address?.neighborhood ?? null;
  cityObjectModel.value.name = props?.address?.city ?? "";
  stateObjectModel.value.name = props?.address?.state ?? "";
  postalCodeModel.value = props?.address?.postalCode ?? null;

  const stateObject = states.value.find(
    (obj: { name: string; code: string }) => {
      return obj.name === stateObjectModel.value.name;
    }
  );
  stateObjectModel.value.code = stateObject?.code ?? "";
  if (stateObjectModel.value.name !== "") {
    fetchCities(stateObjectModel.value.name);
  }

  forceRerender();

  emit("customerAddress", fullAddress.value);
});

onUpdated(() => {
  renderCounter.value++;
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

.vs--searchable {
  padding: 10px;
  height: 40px;
}

.ceara-store-link {
  color: $pj-color-green;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    color: darken($color: $pj-color-green, $amount: 10);
    text-decoration: underline;
  }
}
</style>
